import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, FileText, AlertCircle, PenLine, Inbox, ChevronRight } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { statusLabel, statusTone, fmtDateTime } from '../../lib/docs';
import { toast } from 'sonner';

export default function AdminDocumentsPage() {
  const [dash, setDash] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, m] = await Promise.all([
          api.specialistDashboard(),
          api.specialistMembers(),
        ]);
        setDash(d); setMembers(m);
      } catch { toast.error('Could not load documents data'); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <AdminLayout title="Document Repository" subtitle="Review uploads, request missing items, manage signatures.">
      {loading ? <div className="py-20 text-center text-neutral-500">Loading…</div> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-7">
            <StatCard label="Pending review" value={dash?.pending_review || 0} icon={Inbox} tone="amber" testid="stat-pending" />
            <StatCard label="Needs clarification" value={dash?.needs_clarification || 0} icon={AlertCircle} tone="orange" testid="stat-clar" />
            <StatCard label="Signatures outstanding" value={dash?.signatures_outstanding || 0} icon={PenLine} tone="fuchsia" testid="stat-sig" />
            <StatCard label="Missing items" value={dash?.missing_outstanding || 0} icon={FileText} tone="neutral" testid="stat-missing" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white rounded-xl border border-neutral-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
                <h3 className="text-[15px] font-bold text-[#14110f] flex items-center gap-2"><Users className="w-4 h-4 text-[#c89200]" /> Members</h3>
                <span className="text-[12px] text-neutral-500">{members.length} total</span>
              </div>
              {members.length === 0 ? <p className="px-5 py-10 text-center text-[13px] text-neutral-500">No members assigned to you yet.</p>
                : <table className="w-full text-[13.5px]" data-testid="members-table">
                    <thead className="bg-neutral-50 text-[11.5px] uppercase tracking-wider text-neutral-600">
                      <tr><th className="text-left px-5 py-2.5">Name</th><th className="text-left px-5 py-2.5">Email</th><th className="text-right px-5 py-2.5">Docs</th><th className="text-right px-5 py-2.5">Pending</th><th></th></tr>
                    </thead>
                    <tbody>
                      {members.map((m) => (
                        <tr key={m.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                          <td className="px-5 py-3 font-semibold text-[#14110f]">{m.name}</td>
                          <td className="px-5 py-3 text-neutral-600">{m.email}</td>
                          <td className="px-5 py-3 text-right text-neutral-700">{m.document_count}</td>
                          <td className="px-5 py-3 text-right">
                            {m.pending_review > 0
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700">{m.pending_review}</span>
                              : <span className="text-neutral-400">—</span>}
                          </td>
                          <td className="px-5 py-3 text-right">
                            <Link to={`/admin/members/${m.id}/tax-file`} className="text-[12px] font-semibold text-[#c89200] hover:underline inline-flex items-center gap-1">
                              Open <ChevronRight className="w-3.5 h-3.5" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>}
            </div>

            <div className="bg-white rounded-xl border border-neutral-200">
              <div className="px-5 py-4 border-b border-neutral-100">
                <h3 className="text-[15px] font-bold text-[#14110f] flex items-center gap-2"><Inbox className="w-4 h-4 text-[#c89200]" /> Recent uploads</h3>
              </div>
              <ul className="divide-y divide-neutral-100">
                {(dash?.recent_uploads || []).length === 0 ? <li className="px-5 py-8 text-center text-[13px] text-neutral-500">No recent uploads</li>
                  : dash.recent_uploads.map((d) => (
                    <li key={d.id} className="px-5 py-3" data-testid="recent-upload">
                      <Link to={`/admin/documents/${d.id}`} className="block hover:opacity-80">
                        <p className="text-[13px] font-semibold text-[#14110f] truncate">{d.original_name}</p>
                        <p className="text-[11.5px] text-neutral-500 truncate">{d.member_name} · {d.category_name} · {d.tax_year}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${statusTone(d.status)}`}>{statusLabel(d.status)}</span>
                          <span className="text-[11px] text-neutral-400">{fmtDateTime(d.uploaded_at)}</span>
                        </div>
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

function StatCard({ label, value, icon: Icon, tone, testid }) {
  const tones = {
    amber: 'bg-amber-50 text-amber-700',
    orange: 'bg-orange-50 text-orange-700',
    fuchsia: 'bg-fuchsia-50 text-fuchsia-700',
    neutral: 'bg-neutral-100 text-neutral-700',
  };
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-neutral-200 p-5">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
      <p className="text-[28px] font-extrabold text-[#14110f] mt-3 leading-none">{value}</p>
      <p className="text-[12.5px] text-neutral-500 mt-1">{label}</p>
    </div>
  );
}
